import React, { useCallback, useMemo, useId } from 'react';
import { TextField } from '@mui/material';
import FieldLabel from './FieldLabel';
import { ILabel } from '../constants/interfaces';
import {
  getTextAreaStyles,
  getAccessibilityProps,
  getFormHelperTextSlotProps,
  getFieldName,
  inputLimits,
} from '../styles/fieldStyles';

/**
 * Props for the TextAreaField component
 */
export interface ITextAreaFieldProps {
  /** Current text value */
  value: string;
  /** Callback fired when the text value changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Width of the text area */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Minimum number of visible rows */
  minRows?: number;
  /** Maximum number of visible rows */
  maxRows?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Auto-resize behavior */
  autoResize?: boolean;
  /** Callback fired when textarea gains focus */
  onFocus?: () => void;
  /** Callback fired when textarea loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for textarea handling
 */
const textAreaUtils = {
  /**
   * Counts lines in the text
   */
  countLines: (text: string): number => {
    return text.split('\n').length;
  },

  /**
   * Counts words in the text
   */
  countWords: (text: string): number => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  },

  /**
   * Gets character count display text
   */
  getCharacterCountText: (current: number, max: number): string => {
    return `${current}/${max} characters`;
  },

  /**
   * Sanitizes textarea value
   */
  sanitizeValue: (value: string): string => {
    // Remove any null bytes or other problematic characters
    return value.replace(/\0/g, '');
  },

  /**
   * Validates textarea value based on field requirements
   */
  validateText: (
    value: string,
    field: ILabel,
    maxLength: number
  ): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }

    if (value.length > maxLength) {
      return `${field.text} must be ${maxLength} characters or less`;
    }

    return null;
  },
};

/**
 * TextAreaField Component
 *
 * A reusable multi-line text input component that integrates with form validation
 * and provides a consistent interface for text input across the application.
 *
 * Features:
 * - Multi-line text input with auto-resize capability
 * - Character count and validation
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Configurable rows and resize behavior
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <TextAreaField
 *   value={description}
 *   onChange={handleDescriptionChange}
 *   field={descriptionField}
 *   errorText={validationError}
 *   placeholder="Enter detailed description"
 *   minRows={3}
 *   maxRows={8}
 * />
 * ```
 */
function TextAreaField(props: ITextAreaFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    minRows = inputLimits.minRows.textarea,
    maxRows = inputLimits.maxRows.textarea,
    maxLength = inputLimits.maxLength.textarea,
    autoResize = true,
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize computed values
  const computedValues = useMemo(() => {
    const hasError = Boolean(errorText);
    const characterCount = value.length;
    const wordCount = textAreaUtils.countWords(value);
    const lineCount = textAreaUtils.countLines(value);
    const characterCountText = textAreaUtils.getCharacterCountText(
      characterCount,
      maxLength
    );
    const effectiveMaxLength = Math.min(maxLength, 32768); // Ensure reasonable limit

    return {
      characterCount,
      characterCountText,
      effectiveMaxLength,
      hasError,
      isDisabled: disabled,
      isNearLimit: characterCount > effectiveMaxLength * 0.9, // Warning when 90% full
      lineCount,
      wordCount,
    };
  }, [errorText, value, maxLength, disabled]);

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const sanitizedValue = textAreaUtils.sanitizeValue(newValue);

      // Enforce character limit
      if (sanitizedValue.length <= computedValues.effectiveMaxLength) {
        onChange(sanitizedValue);
      }
    },
    [onChange, computedValues.effectiveMaxLength]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  // Generate helper text for accessibility
  const helperText = useMemo(() => {
    if (errorText) return errorText;
    return computedValues.characterCountText;
  }, [errorText, computedValues.characterCountText]);

  return (
    <>
      {/* Label with proper accessibility attributes */}
      <FieldLabel field={field} htmlFor={fieldId} isHidden={isLabelHidden} />

      {/* Textarea with enhanced props */}
      <TextField
        id={fieldId}
        fullWidth={width === '100%'}
        name={getFieldName(field)}
        size="small"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={computedValues.isDisabled}
        error={computedValues.hasError}
        helperText={helperText}
        placeholder={placeholder}
        multiline
        minRows={minRows}
        maxRows={autoResize ? maxRows : minRows}
        sx={getTextAreaStyles(width, computedValues.isNearLimit)}
        inputProps={{
          maxLength: computedValues.effectiveMaxLength,
          ...getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          ),
          readOnly: readOnly,
          style: { resize: autoResize ? 'vertical' : 'none' },
        }}
        slotProps={getFormHelperTextSlotProps(computedValues.hasError, errorId)}
      />
    </>
  );
}

export default TextAreaField;

// Export utilities for testing and external use
export { textAreaUtils };
